import * as React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { toProductShape } from 'lib/util/productShape';
import { Product } from 'models/product';
import { getThemeCss } from 'components/Themes';

import { AccordionList } from './AccordionList/AccordionList';
import { Highlights } from './Highlights/Highlights';
import { MediaCarousel } from './MediaCarousel/MediaCarousel';
import { OperationInfo } from './OperationInfo/OperationInfo';
import { ProductOutline } from './ProductOutline/ProductOutline';
import { ServiceList } from './ServiceList/ServiceList';
import { TourInformation } from './TourInformation/TourInformation';
import { AccordionItemContent } from './AccordionList/AccordionItem';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { ProductContentsSectionHeader } from './ProductContentsSectionHeader';
import { ProductContentsHeader } from 'components/ProductContentsHeader/ProductContentsHeader';

interface OwnProps {
  product: Product;
}

type Props = OwnProps;

export const ProductContentsPane = ({ product: apiProduct }: Props) => {
  const product = toProductShape(apiProduct);
  const { t } = useTranslation();

  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);
  const showMediaCarousel = product.mediaURLs.length > 0;
  const showHighlights = product.highlights.length > 0;
  const showOutline =
    product.schedule.length > 0 ||
    product.pickupLocations.length > 0 ||
    product.checkinLocations.length > 0 ||
    product.dropoffLocations.length > 0 ||
    product.checkoutLocations.length > 0;
  const showAddonsAndTransportation =
    product.addOns.length > 0 || product.transportations.length > 0;

  // Avoid to close the 'Show All'  part when scroll down the page at mobile view
  const addOnsAccordionListItems = React.useMemo(() => {
    const addOnsAccordionListItems: AccordionItemContent[] = [];
    if (product.addOns.length > 0) {
      addOnsAccordionListItems.push({
        header: t('Add-ons'),
        content: () => (
          <ServiceList
            services={product.addOns}
            hideOverThresholdServices={true}
            threshold={3}
            showAllText={t('Show All Add-ons')}
          />
        ),
        isActiveDefault: true,
      });
    }

    if (product.transportations.length > 0) {
      addOnsAccordionListItems.push({
        header: t('Transportation'),
        content: () => <ServiceList services={product.transportations} />,
        isActiveDefault: true,
      });
    }
    return addOnsAccordionListItems;
  }, [t]);

  const showProductHeaderBelowImages = bookingWidgetTheme === 'PACIFIC';

  return (
    <div
      className={clsx('base-single__card', getThemeCss(bookingWidgetTheme, 'base-single__card'))}
    >
      {showMediaCarousel && (
        <section>
          <MediaCarousel urls={product.mediaURLs} />
        </section>
      )}
      {showProductHeaderBelowImages && <ProductContentsHeader product={apiProduct} />}
      <OperationInfo product={product} />
      {showHighlights && (
        <section className="base-single__section">
          <ProductContentsSectionHeader stylizedText="Highlight " text={t('Highlights')} />
          <div className="base-padding-16">
            <Highlights highlights={product.highlights} />
          </div>
        </section>
      )}
      {showOutline && (
        <section className="base-single__section">
          <ProductContentsSectionHeader stylizedText="Outline " text={t('Outline')} />
          <div className="base-padding-16">
            <ProductOutline product={product} />
          </div>
        </section>
      )}
      {showAddonsAndTransportation && (
        <section className="base-single__section">
          <ProductContentsSectionHeader
            stylizedText="Transfer/Options "
            text={t('Add-ons / Transportation')}
          />
          <div className="base-padding-16">
            <AccordionList items={addOnsAccordionListItems} />
          </div>
        </section>
      )}
      <section className="base-single__section">
        <ProductContentsSectionHeader
          stylizedText="Plan Information "
          text={t('Plan Information')}
        />
        <div className="base-padding-16">
          <TourInformation product={product} />
        </div>
      </section>
    </div>
  );
};
